import {
  buildGridRedirectHref,
  buildLegacySegmentRedirectHref,
  firstCsvValue,
  parseEmailsPath,
} from '@/lib/emails/emails-route'

export type SplitHandoff =
  | { kind: 'exact'; pathname: string }
  | { kind: 'email-resolver'; href: string }

/** Route/query identity, excluding fragments and query-key ordering. */
export function normalizeSplitHandoffHref(href: string): string {
  const url = new URL(href, 'https://brew.invalid')
  url.searchParams.sort()
  const search = url.searchParams.toString()
  return url.pathname + (search ? `?${search}` : '')
}

/** Known group targets remain exact; email point and legacy grid URLs resolve. */
export function createSplitHandoff(href: string): SplitHandoff {
  const requested = new URL(href, 'https://brew.invalid')
  const route = parseEmailsPath(requested.pathname)
  if (
    route.kind === 'legacy-email' ||
    (requested.pathname === '/emails' &&
      (firstCsvValue(requested.searchParams.get('emailIds')) ||
        firstCsvValue(requested.searchParams.get('focusEmailIds'))))
  ) {
    return {
      kind: 'email-resolver',
      href: requested.pathname + requested.search,
    }
  }
  return { kind: 'exact', pathname: requested.pathname }
}

function sameQuery(left: URLSearchParams, right: URLSearchParams): boolean {
  const normalizedLeft = new URLSearchParams(left)
  const normalizedRight = new URLSearchParams(right)
  normalizedLeft.sort()
  normalizedRight.sort()
  return normalizedLeft.toString() === normalizedRight.toString()
}

/**
 * Recognize the page redirect's exact query for panel presentation only.
 * Group ownership and authorization remain with the server page resolver.
 * An intermediate grid commit does not spend the final destination handoff.
 */
export function resolveSplitHandoff(
  handoff: SplitHandoff | null,
  destinationHref: string
): 'intermediate' | 'arrived' | 'cancelled' {
  if (!handoff) {
    return 'cancelled'
  }
  const destination = new URL(destinationHref, 'https://brew.invalid')
  if (handoff.kind === 'exact') {
    return handoff.pathname === destination.pathname ? 'arrived' : 'cancelled'
  }
  const requested = new URL(handoff.href, 'https://brew.invalid')
  if (destination.pathname === requested.pathname) {
    return sameQuery(requested.searchParams, destination.searchParams)
      ? 'intermediate'
      : 'cancelled'
  }
  const route = parseEmailsPath(destination.pathname)
  if (route.kind !== 'group' && route.kind !== 'ungrouped') {
    return 'cancelled'
  }
  const groupSegment = route.kind === 'group' ? route.groupId : 'ungrouped'
  const requestedRoute = parseEmailsPath(requested.pathname)
  const canonicalHref =
    requestedRoute.kind === 'legacy-email'
      ? buildLegacySegmentRedirectHref({
          segment: requestedRoute.emailId,
          search: requested.searchParams,
          resolvedGroupSegment: groupSegment,
        })
      : buildGridRedirectHref({
          search: requested.searchParams,
          focusGroupSegment: groupSegment,
        })
  if (!canonicalHref) {
    return 'cancelled'
  }
  const canonical = new URL(canonicalHref, 'https://brew.invalid')
  return canonical.pathname === destination.pathname &&
    sameQuery(canonical.searchParams, destination.searchParams)
    ? 'arrived'
    : 'cancelled'
}
